import { Popover, PopoverContent, PopoverItem, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Button } from "@/components/ui/baseUi/button"
import { appToast as toast, warningToastClassName } from "@/components/ui/baseUi/sonner"
import { useCalendar } from "@/modules/calendar/hooks/useCalendar"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import {
  ProgramWeekCopyModal,
  type ProgramWeekCopyModalRef,
} from "@/modules/programs/components/programHeader/programWeekCopyModal"
import { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { useProgram } from "@/modules/programs/hooks/usePrograms"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { useProgramSessionWeekDuplicate } from "@/modules/sessions/hooks/useProgramSessionWeekDuplicate"
import {
  useTemplateWeekImportSidebar,
  useTemplateSaveWeekFormSidebar,
} from "@/modules/template/hooks/useTemplateSidebar"
import { useProgramSessionEvents } from "@/modules/training/hooks/useTraining"
import dayjs, { isDayjs } from "dayjs"
import { CalendarArrowUpIcon, CalendarCheck2Icon, ChevronDownIcon, ClipboardIcon, CopyIcon } from "lucide-react"
import { useMemo, useRef } from "react"

type ProgramHeaderRightActionsButtonProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramHeaderRightActionsButton({
  programId,
  className,
  ...props
}: ProgramHeaderRightActionsButtonProps) {
  const programWeekCopyModalRef = useRef<ProgramWeekCopyModalRef>(null)
  const { navigateToTemplateWeekImport } = useTemplateWeekImportSidebar()
  const { navigateToTemplateSaveWeekForm } = useTemplateSaveWeekFormSidebar()
  const { data: program } = useProgram(programId)
  const { onCopyInfiniteProgramWeek, onCopyOnOffProgramWeek, onCopyProProgramWeek } = useProgramSessionWeekDuplicate()

  const { getCalendarRange, selectedDate } = useCalendar()
  const { startDate, endDate } = getCalendarRange()

  const calendarRange = useMemo(() => {
    if (isDayjs(startDate) && isDayjs(endDate)) {
      return {
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).toISOString(),
      }
    } else if (typeof startDate === "number" && typeof endDate === "number") {
      return {
        startDate: startDate,
        endDate: endDate,
      }
    } else return undefined
  }, [startDate, endDate])

  const {
    data: { sessions: programSessions },
  } = useProgramSessionEvents({
    programId: programId,
    programType: UserProgramDetailsEntity.isSessionsProgramType(program.type) ? program.type : undefined,
    startDate: calendarRange?.startDate,
    endDate: calendarRange?.endDate,
  })

  const isWeekHasSessions = programSessions.length > 0

  const onCopyWeekClick = () => {
    if (isWeekHasSessions) {
      if (program.type === ProgramType.VIRTUAL && !!selectedDate && typeof selectedDate !== "number") {
        onCopyInfiniteProgramWeek(
          program.id,
          ProgramType.VIRTUAL,
          program.name,
          dayjs(selectedDate).weekday(0).toISOString(),
          dayjs(selectedDate).weekday(6).toISOString(),
        )
      } else if (program.type === ProgramType.ONOFF && typeof selectedDate === "number") {
        onCopyOnOffProgramWeek(program.id, ProgramType.ONOFF, program.name, selectedDate, selectedDate + 6)
      } else if (program.type === ProgramType.PRO && !!selectedDate && typeof selectedDate !== "number") {
        onCopyProProgramWeek(
          program.id,
          ProgramType.PRO,
          program.name,
          dayjs(selectedDate).weekday(0).toISOString(),
          dayjs(selectedDate).weekday(6).toISOString(),
        )
      }
    } else {
      toast.warning(word("program.weekCopyPaste.copy.no_sessions_error"), {
        classNames: warningToastClassName(),
      })
    }
  }

  const onPasteWeekClick = async () => {
    await programWeekCopyModalRef.current?.onPasteWeekClick()
  }

  return (
    <>
      <div className={cn("flex flex-row gap-1", className)} {...props}>
        <Popover>
          <PopoverTrigger
            render={
              <Button variant="secondarySecondary" size="md" className="flex-1">
                {word("template.title.text.weekTemplate")}
                <ChevronDownIcon />
              </Button>
            }
          />
          <PopoverContent align="start">
            <PopoverItem
              onClick={() => {
                navigateToTemplateWeekImport(programId, {
                  startDate: isDayjs(startDate) ? startDate.toDate() : null,
                  startIndex: typeof startDate === "number" ? startDate : null,
                })
              }}
            >
              <CalendarArrowUpIcon />
              {word("template.week.import.button")}
            </PopoverItem>
            <PopoverItem
              disabled={!isWeekHasSessions}
              onClick={() => {
                navigateToTemplateSaveWeekForm(
                  { sessionIds: programSessions.map((session) => session.id) },
                  { replaceAllComponents: true },
                )
              }}
            >
              <CalendarCheck2Icon />
              {word("template.week.save.button")}
            </PopoverItem>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger
            render={
              <Button variant="secondarySecondary" size="md" className="flex-1">
                {word("program.weekCopyPaste.copy.button")}
                <ChevronDownIcon />
              </Button>
            }
          />
          <PopoverContent align="start">
            <PopoverItem
              onClick={() => {
                onCopyWeekClick()
              }}
              disabled={!isWeekHasSessions}
            >
              <CopyIcon />
              {word("program.weekCopyPaste.copy.button")}
            </PopoverItem>
            <PopoverItem onClick={() => void onPasteWeekClick()}>
              <ClipboardIcon />
              {word("program.weekCopyPaste.paste.button")}
            </PopoverItem>
          </PopoverContent>
        </Popover>
      </div>

      <ProgramWeekCopyModal program={program} calendarRange={calendarRange} ref={programWeekCopyModalRef} />
    </>
  )
}

type ProgramHeaderRightActionsButtonSuspenseProps = {
  className?: string
} & React.ComponentProps<"div">

export function ProgramHeaderRightActionsButtonSuspense({
  className,
  ...props
}: ProgramHeaderRightActionsButtonSuspenseProps) {
  return (
    <div className={cn("flex flex-row gap-1", className)} {...props}>
      <Popover>
        <PopoverTrigger
          render={
            <Button variant="secondarySecondary" size="md" className="flex-1">
              {word("template.title.text.weekTemplate")}
              <ChevronDownIcon />
            </Button>
          }
        />
        <PopoverContent align="start">
          <PopoverItem disabled>
            <CalendarArrowUpIcon />
            {word("template.week.import.button")}
          </PopoverItem>
          <PopoverItem disabled>
            <CalendarCheck2Icon />
            {word("template.week.save.button")}
          </PopoverItem>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger
          render={
            <Button variant="secondarySecondary" size="md" className="flex-1">
              {word("program.weekCopyPaste.copy.button")}
              <ChevronDownIcon />
            </Button>
          }
        />
        <PopoverContent align="start">
          <PopoverItem disabled>
            <CopyIcon />
            {word("program.weekCopyPaste.copy.button")}
          </PopoverItem>
          <PopoverItem disabled>
            <ClipboardIcon />
            {word("program.weekCopyPaste.paste.button")}
          </PopoverItem>
        </PopoverContent>
      </Popover>
    </div>
  )
}
